/**
 * Mundalo - UTM capture helper (iter 144h: first-touch wins).
 *
 * Reads utm_source / utm_medium / utm_campaign / utm_content from the URL
 * on first paint and persists them in localStorage. **First touch wins** -
 * once a UTM payload is stored we do not overwrite it on subsequent
 * visits even if new UTMs are present in the URL. This preserves the
 * originating attribution for the entire user lifetime (subject to a
 * 30-day TTL so stale attribution rolls off naturally).
 *
 * `attachUtmToUser({ email })` fires a fire-and-forget PATCH to
 * `/api/users/utm` and is safe to call from anywhere; it no-ops when
 * there's no stored UTM block.
 */
const KEY = 'mundalo_utm';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const API = process.env.REACT_APP_BACKEND_URL || '';

const _readStoredRaw = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj) return null;
    if (obj.captured_at && (Date.now() - obj.captured_at) > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return obj;
  } catch { return null; }
};

export const captureUtmFromUrl = () => {
  try {
    // iter 144h - first touch wins. If any stored UTM block already
    // exists (and is non-expired) we skip even when new UTMs are present.
    const existing = _readStoredRaw();
    if (existing && (existing.utm_source || existing.utm_medium || existing.utm_campaign || existing.utm_content)) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const source   = (params.get('utm_source')   || '').trim().toLowerCase();
    const medium   = (params.get('utm_medium')   || '').trim().toLowerCase();
    const campaign = (params.get('utm_campaign') || '').trim().toLowerCase();
    const content  = (params.get('utm_content')  || '').trim().toLowerCase();
    if (!source && !medium && !campaign && !content) return;
    const payload = {
      utm_source:   source   || null,
      utm_medium:   medium   || null,
      utm_campaign: campaign || null,
      utm_content:  content  || null,
      captured_at:  Date.now(),
    };
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch { /* localStorage disabled - ignore */ }
};

export const getUtm = () => {
  const obj = _readStoredRaw();
  if (!obj) return {};
  return {
    utm_source:   obj.utm_source   || undefined,
    utm_medium:   obj.utm_medium   || undefined,
    utm_campaign: obj.utm_campaign || undefined,
    utm_content:  obj.utm_content  || undefined,
  };
};

export const clearUtm = () => {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
};

/** iter 144h - fire-and-forget PATCH to attach the stored UTM block to
 *  a user record. Resolves the user via JWT (when `useAuth=true`, the
 *  default after sign-in) or by `email` body field (for tool-lead flows
 *  where the user does not yet hold a JWT). Safe to call from anywhere
 *  - no-ops when there's no stored UTM payload.
 */
export const attachUtmToUser = async ({ email = null, useAuth = true } = {}) => {
  const u = getUtm();
  if (!u.utm_source && !u.utm_medium && !u.utm_campaign && !u.utm_content) return;
  const body = {
    utm_source:   u.utm_source   || '',
    utm_medium:   u.utm_medium   || '',
    utm_campaign: u.utm_campaign || '',
    utm_content:  u.utm_content  || '',
  };
  if (email) body.email = String(email).toLowerCase().trim();
  try {
    await fetch(`${API}/api/users/utm`, {
      method: 'PATCH',
      credentials: useAuth ? 'include' : 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch { /* never block UX on attribution */ }
};
